import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Filter, Download, RefreshCw, Target, TrendingUp, AlertCircle } from 'lucide-react';
import axios from 'axios';

const SalesTargetList = () => {
  const navigate = useNavigate();
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    targetLevel: '',
    periodType: '',
    periodYear: new Date().getFullYear().toString(),
    status: 'Active'
  });
  const [summary, setSummary] = useState({});

  useEffect(() => {
    fetchTargets();
    fetchSummary();
  }, [filters]);

  const fetchTargets = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.targetLevel) params.append('targetLevel', filters.targetLevel);
      if (filters.periodType) params.append('periodType', filters.periodType);
      if (filters.periodYear) params.append('periodYear', filters.periodYear);
      if (filters.status) params.append('status', filters.status);

      const response = await axios.get(`/api/salestargets?${params.toString()}`);
      const fetchedTargets = response.data.targets;
      
      // Ensure targets is always an array
      if (Array.isArray(fetchedTargets)) {
        setTargets(fetchedTargets);
      } else {
        console.warn('API returned non-array targets:', fetchedTargets);
        setTargets([]);
      }
    } catch (error) {
      console.error('Error fetching sales targets:', error);
      setTargets([]); // Ensure targets is set to empty array on error
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.periodYear) params.append('periodYear', filters.periodYear);

      const response = await axios.get(`/api/salestargets/summary?${params.toString()}`);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleReset = () => {
    setFilters({
      targetLevel: '',
      periodType: '',
      periodYear: new Date().getFullYear().toString(),
      status: 'Active'
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      Draft: 'bg-gray-100 text-gray-800',
      Active: 'bg-green-100 text-green-800',
      Completed: 'bg-blue-100 text-blue-800',
      Cancelled: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const getLevelBadge = (level) => {
    const badges = {
      Individual: 'bg-blue-100 text-blue-800',
      Team: 'bg-purple-100 text-purple-800',
      Company: 'bg-green-100 text-green-800'
    };
    return badges[level] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <Target className="w-6 h-6 mr-2" />
              Sales Targets
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Manage and track sales targets across your organization
            </p>
          </div>
          <button
            onClick={() => navigate('/salestargets/new')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Target
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Total Targets</p>
                <p className="text-2xl font-bold text-gray-900">{summary.TotalTargets || 0}</p>
              </div>
              <Target className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Active</p>
                <p className="text-2xl font-bold text-green-600">{summary.ActiveTargets || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Completed</p>
                <p className="text-2xl font-bold text-blue-600">{summary.CompletedTargets || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-400" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500 uppercase">Draft</p>
                <p className="text-2xl font-bold text-gray-600">{summary.DraftTargets || 0}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center">
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </h3>
            <div className="flex space-x-2">
              <button
                onClick={handleReset}
                className="text-xs text-gray-600 hover:text-gray-900"
              >
                Reset
              </button>
              <button
                onClick={fetchTargets}
                className="text-xs text-blue-600 hover:text-blue-900 flex items-center"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Refresh
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Target Level
              </label>
              <select
                value={filters.targetLevel}
                onChange={(e) => handleFilterChange('targetLevel', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Levels</option>
                <option value="Individual">Individual</option>
                <option value="Team">Team</option>
                <option value="Company">Company</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Period Type
              </label>
              <select
                value={filters.periodType}
                onChange={(e) => handleFilterChange('periodType', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                value={filters.periodYear}
                onChange={(e) => handleFilterChange('periodYear', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="Draft">Draft</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
              <p className="text-sm text-gray-600">Loading targets...</p>
            </div>
          </div>
        ) : targets.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Target className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-gray-900">No targets found</p>
              <p className="text-xs text-gray-500 mt-1">Create a new target to get started</p>
              <button
                onClick={() => navigate('/salestargets/new')}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create First Target
              </button>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Dates
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(targets) && targets.map((target) => (
                  <tr
                    key={target.TargetID}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/salestargets/${target.TargetID}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {target.TargetName}
                          </div>
                          {target.Description && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {target.Description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelBadge(target.TargetLevel)}`}>
                        {target.TargetLevel}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{target.PeriodType}</div>
                        <div className="text-xs text-gray-500">
                          {target.PeriodYear}
                          {target.PeriodQuarter && ` Q${target.PeriodQuarter}`}
                          {target.PeriodMonth && ` M${target.PeriodMonth}`}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="text-xs">
                        {formatDate(target.StartDate)} - {formatDate(target.EndDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(target.Status)}`}>
                        {target.Status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/salestargets/${target.TargetID}`);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTargetList;


